import { Button } from "@/src/components/ui/button";
import { ComboboxWithAdd } from "@/src/components/ui/combobox-with-add";
import { DatePicker } from "@/src/components/ui/date-picker";
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from "@/src/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { InputNumber } from "@/src/components/ui/input-number";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { toast, useToast } from "@/src/components/ui/use-toast";
import { useEditBookingPaymentMutation } from "@/src/lib/bookingEditPaymentMutation";
import { paymentsFetch } from "@/src/lib/paymentsFetch";
import { useAsyncList } from "@/src/lib/useAsyncList";
import { zodResolver } from "@hookform/resolvers/zod";
import { Separator } from "@radix-ui/react-separator";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const createSchema = (bookingAmount: number, paidSoFar: number, initialAmount: number) =>
  z
    .object({
      bookingId: z.string(),
      payerType: z.enum(["Customer", "Operator"]),
      paymentType: z.string(),
      account: z.number().optional(),
      amount: z.number(),
      amountCurrency: z.enum(["EUR", "GBP", "USD", "CAD", "AUD", "NZD"]),
      receivingBookingAmount: z.number(),
      receivingBookingCurrency: z.enum(["EUR", "GBP", "USD", "CAD", "AUD", "NZD"]),
      date: z.date(),
      status: z.enum(["APPROVED", "PENDING", "DECLINED"]),
    })
    .superRefine((data, ctx) => {
      const totalPaid = data.receivingBookingAmount + paidSoFar - initialAmount;
      console.log("totalPaid", totalPaid, bookingAmount, initialAmount);

      if (totalPaid > bookingAmount) {
        ctx.addIssue({
          path: ["receivingBookingAmount"],
          message: "Total paid amount cannot exceed the booking amount.",
          code: z.ZodIssueCode.custom,
        });
        toast({
          description: "Total paid amount cannot exceed the booking amount.",
          variant: "destructive",
        });
      }
    });
type PaymentInput = z.infer<ReturnType<typeof createSchema>>;

export function EditBookingPayment({
  initData,
  bookingId,
  payerType,
  paymentData,
  operatorCurrencyCode
}: {
  initData: any;
  bookingId: string;
  payerType: "Customer" | "Operator";
  paymentData: {
    bookingAmount: number;
    paidSoFar: number;
  };
  operatorCurrencyCode?: "EUR" | "GBP" | "USD" | "CAD" | "AUD" | "NZD";
}) {
  const currencyCodes = useAsyncList<string>("/api/currencies/codes");

  const payerTypes = ["Customer", "Operator"];

  const paymentTypes = ["Bank transfer", "Cash"];

  const paymentStatus = ["APPROVED", "PENDING", "DECLINED"];

  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any | null>(null);

  const editPaymentMutation = useEditBookingPaymentMutation(bookingId);

  const schema = createSchema(paymentData.bookingAmount, paymentData.paidSoFar, Number(initData?.amount));

  const form = useForm<PaymentInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      bookingId,
      payerType,
      paymentType: initData?.paymentType ?? undefined,
      account: initData?.bankAccount?.id ?? undefined,
      amount: initData?.amountBookingCurrency ? Number(initData?.amountBookingCurrency) : Number(initData?.amount),
      amountCurrency: initData?.currency || initData?.booking?.currency?.code || "EUR",
      receivingBookingAmount: initData?.amount ? Number(initData?.amount) : undefined,
      receivingBookingCurrency: (payerType  === "Operator" ?  operatorCurrencyCode : initData?.booking?.currency?.code) || "EUR",
      date: new Date(initData?.paymentDate),
      status: initData?.status ?? "APPROVED",
    },
  });

  useEffect(() => {
    if (initData.bankAccount) {
      setSelectedAccount(initData.bankAccount);
    }
  }, [initData.bankAccount]);

  useEffect(() => {
    form.reset({
      bookingId,
      payerType,
      paymentType: initData?.paymentType ?? undefined,
      account: initData?.bankAccount?.id ?? undefined,
      amount: initData?.amountBookingCurrency ? Number(initData?.amountBookingCurrency) : Number(initData?.amount),
      amountCurrency: initData?.currency || initData?.booking?.currency?.code || "EUR",
      receivingBookingAmount: initData?.amount ? Number(initData?.amount) : undefined,
      receivingBookingCurrency: (payerType  === "Operator" ?  operatorCurrencyCode : initData?.booking?.currency?.code) || "EUR",
      date: initData?.paymentDate ? new Date(initData?.paymentDate) : undefined,
      status: initData?.status ?? "APPROVED",
    });
  }, [initData, form.reset]);

  const paymentType = form.watch("paymentType");

  const onSubmit = form.handleSubmit(async (data) => {
    const body = {
      bookingId: data.bookingId,
      payerType: data.payerType,
      paymentType: data.paymentType,
      bankAccountId: data.account,
      amount: data.receivingBookingAmount ? data.receivingBookingAmount : data.amount,
      amountBookingCurrency: data.amount,
      amountCurrency: data.amountCurrency,
      paymentDate: data.date !== initData?.paymentDate ? data.date.setHours(12, 0, 0, 0) : initData?.paymentDate,
      status: data.status,
    };

    const toastId = toast({
      description: "Adding payment...",
      variant: "default",
      duration: Infinity,
    }).id;

    editPaymentMutation.mutate(
      { body, paymentId: initData?.id, toastId },
      {
        onSuccess: () => {
          form.reset();
          setOpen(false);
        },
      },
    );
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <p className="text-sm text-blue-600 font-medium cursor-pointer ml-auto pr-2 pl-2 pt-4 pb-4">Edit</p>
      </DialogTrigger>
      <DialogContent
        className="max-w-xl"
        style={{
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <DialogHeader>
          <h2 className="text-3xl">Payment</h2>
        </DialogHeader>
        <Separator />
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={onSubmit}>
            <FormField<PaymentInput, "bookingId">
              name="bookingId"
              render={({ field }) => (
                <FormItem className="flex-grow">
                  <FormLabel className="mb-1">Booking ID</FormLabel>
                  <FormControl>
                    <Input disabled={true} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex items-start gap-4">
              <FormField<PaymentInput, "payerType">
                name="payerType"
                render={({ field: { value, onChange } }) => (
                  <FormItem className="flex-grow">
                    <FormLabel>Payment made by</FormLabel>
                    <Select onValueChange={onChange} defaultValue={value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {payerTypes.map((type) => (
                          <SelectItem key={type} value={type} disabled>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField<PaymentInput, "paymentType">
                name="paymentType"
                render={({ field: { value, onChange } }) => (
                  <FormItem className="flex-grow">
                    <FormLabel>Payment type</FormLabel>
                    <Select onValueChange={onChange} defaultValue={value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {paymentTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>

            {paymentType === "Bank transfer" && (
              <FormField<PaymentInput, "account">
                name="account"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Account</FormLabel>
                    <FormControl>
                      <ComboboxWithAdd<{ id: number; name: string; number: string }>
                        className="w-full"
                        value={selectedAccount}
                        onChange={(account) => {
                          onChange(account ? account.id : "");
                          setSelectedAccount(account);
                        }}
                        loadOptions={(query) =>
                          paymentsFetch<{ id: number; name: string; number: string }[]>(
                            `/api/bank-accounts?filter=${encodeURIComponent(query)}`,
                            "GET",
                          ).then((r) => {
                            return r;
                          })
                        }
                        renderOption={(account) => `${account.name} • ${account.number}`}
                        placeholder="Select or add an account"
                        allowAddNewItem={true}
                        onCreate={async (name, number) => {
                          try {
                            const newAccount = await paymentsFetch<{ id: number; name: string; number: string }>(
                              `/api/bank-accounts`,
                              "POST",
                              { name, number },
                            );

                            toast({
                              title: "Bank Account",
                              description: "The bank account has been created",
                              variant: "default",
                            });

                            paymentsFetch<{ id: number; name: string; number: string }[]>(
                              `/api/bank-accounts`,
                              "GET",
                            ).then(() => {
                              setSelectedAccount(newAccount);
                              onChange(newAccount.id);
                            });
                          } catch (error) {
                            console.error("Error creating bank account:", error);

                            toast({
                              title: "Error",
                              description: "Failed to create the bank account. Please try again.",
                              variant: "destructive",
                            });
                          }
                        }}
                        createLabel={(inputValue) => `Add new bank account`}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <FormItem>
              <div className="flex items-start gap-4">
                <FormField<PaymentInput, "amount">
                  name="amount"
                  render={({ field }) => (
                    <FormItem className="flex-grow">
                      <FormLabel className="mb-1">Amount</FormLabel>
                      <FormControl>
                        <InputNumber type="number" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div style={{ width: "84px" }}>
                  <FormItem>
                    <FormLabel className="mb-1">Currency</FormLabel>
                    <FormField<PaymentInput, "amountCurrency">
                      name="amountCurrency"
                      render={({ field: { value, onChange } }) => (
                        <FormControl>
                          <Select onValueChange={onChange} defaultValue={value}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {currencyCodes.map((code) => (
                                <SelectItem key={code} value={code}>
                                  {code}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </FormItem>
                </div>
              </div>
            </FormItem>

            <FormItem>
              <div className="flex items-start gap-4">
                <FormField<PaymentInput, "receivingBookingAmount">
                  name="receivingBookingAmount"
                  render={({ field }) => (
                    <FormItem className="flex-grow">
                      <FormLabel className="mb-1">Receiving in booking currency</FormLabel>
                      <FormControl>
                        <InputNumber type="number" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div style={{ width: "84px" }}>
                  <FormItem>
                    <FormLabel className="mb-1">Currency</FormLabel>
                    <FormField<PaymentInput, "receivingBookingCurrency">
                      name="receivingBookingCurrency"
                      render={({ field: { value, onChange } }) => (
                        <FormControl>
                          <Select onValueChange={onChange} defaultValue={value}>
                            {/** TODO: fetch currency from related booking */}
                            <SelectTrigger disabled>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {currencyCodes.map((code) => (
                                <SelectItem key={code} value={code}>
                                  {code}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </FormItem>
                </div>
              </div>
            </FormItem>

            <FormItem>
              <div className="flex items-start gap-4">
                <FormField<PaymentInput, "date">
                  name="date"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <div>
                        <FormControl>
                          <DatePicker
                            value={field.value ? new Date(field.value) : undefined}
                            onChange={(value) => {
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                      </div>
                      {error && <p className="text-red-500 text-sm mt-1">{error.message}</p>}
                    </FormItem>
                  )}
                />

                <FormField<PaymentInput, "status">
                  name="status"
                  render={({ field: { value, onChange } }) => (
                    <FormItem className="flex-grow">
                      <FormLabel>Payment status</FormLabel>
                      <Select onValueChange={onChange} defaultValue={value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {paymentStatus.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
            </FormItem>

            <div className="mb-2 flex justify-start items-center gap-2">
              <Button className={"ml-auto"} type="submit">
                Submit
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
